import { getlabel } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import {
  contactMatterConnectionTableColumns,
  type ContactMatterConnectionRow,
} from "@/components/features/contact/ContactMatterConnectionsSection/contact-matter-connection-columns";
import { DataTable, Label, ServerErrorFallbackCard } from "@/components/ui";
import { retrieveContactMattersQueryOptions } from "@/lib/data/react-query/matters/matter-contact-connection";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { MatterIcon } from "@/lib/utils/iconUtils";
import { ErrorBoundary } from "@sentry/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type Row } from "@tanstack/react-table";
import pluralize from "pluralize";
import { useMemo } from "react";

interface ContactMatterConnectionsSectionProps {
  contactId: string;
  hideLabel?: boolean;
}
function ContactMatterConnectionsSectionDangerous({
  contactId,
  hideLabel,
}: ContactMatterConnectionsSectionProps) {
  const navigate = useNavigate();

  const { data: mattersData } = useSuspenseQuery(
    retrieveContactMattersQueryOptions(contactId),
  );
  const matterConnections = useMemo(() => {
    return mattersData.data.map((matter) => ({
      matterId: matter.id,
      title: matter.title,
      roles: Array.from(
        new Set(matter.connections.map((connection) => getlabel(connection))),
      ),
    }));
  }, [mattersData.data]);

  return (
    <div>
      {!hideLabel && (
        <div className="flex items-center space-x-2">
          <MatterIcon className="h-5 w-5 text-gray-400" />
          <Label className="text-lg font-semibold">
            {pluralize("Matter", matterConnections.length || 1)}
          </Label>
        </div>
      )}
      <div className="col-span-full">
        <DataTable
          entityLabelPlural="matters"
          noResultsMessage="No matters found."
          onRowClick={async (row: Row<ContactMatterConnectionRow>) => {
            await navigate({
              to: `/matters/${row.original.matterId}`,
            });
          }}
          hideSearchBar
          hideVisiibilityDropdown
          columns={contactMatterConnectionTableColumns}
          data={matterConnections}
          tableName={tanstackTableNames.contactMatterConnections}
        />
      </div>
    </div>
  );
}

export default function ContactMatterConnectionsSection(
  props: ContactMatterConnectionsSectionProps,
) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <ContactMatterConnectionsSectionDangerous {...props} />
    </ErrorBoundary>
  );
}
